// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Styled wrapper intentionally targets the MUI input primitive.
import { Box, Stack, TextField } from '@mui/material';
import MarkdownPreview from '@uiw/react-markdown-preview';
import styled from 'styled-components';

import { TFactoryOperatorStyledProps } from '@/console/features/factory-operator/factory-operator.types';

export const MessagesArea = styled(Stack)`
  && {
    height: 100%;
    max-height: 75vh;
    overflow-y: auto;
    gap: 0.5rem;
    border-radius: ${({ theme }) => theme.shape.containerRadius};
    background-color: ${({ theme }) => theme.palette.background.default};
    padding: 0.75rem;
    border: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

export const MessageWrapper = styled(Box)<TFactoryOperatorStyledProps>`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

export const MessageBubble = styled(Stack)<{ $isUser: boolean }>`
  && {
    max-width: 70%;
    padding: 0.5rem 0.75rem;
    background-color: ${({ theme, $isUser }) =>
      $isUser ? theme.palette.primary.light : theme.palette.background.paper};
    border-radius: ${({ theme }) => theme.shape.tooltipRadius};
    gap: 0.5rem;
  }
`;

// Styled markdown component for chat messages
export const MessageMarkdown = styled(MarkdownPreview)<{ $isUser: boolean }>`
  && {
    background: transparent;
    ${({ theme }) => theme.typography.p8};
    color: ${({ theme }) => theme.palette.text.primary};

    /* Remove default padding */
    padding: 0;
    text-align: left;

    /* Ensure first and last elements have no extra margin */
    & > *:first-child {
      margin-top: 0;
    }

    & > *:last-child {
      margin-bottom: 0;
    }

    /* Paragraphs */
    p {
      margin: 0.5em 0;
      white-space: pre-wrap;
    }

    /* Headers */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0.75em 0 0.5em 0;
      font-weight: 600;
    }

    /* Lists */
    ul,
    ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }

    li {
      margin: 0.25em 0;
    }

    /* Code blocks */
    code {
      font-family: 'Courier New', Courier, monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: ${({ theme }) => theme.palette.background.paper};
      padding: 0.5em;
      border-radius: 4px;
      overflow-x: auto;
    }

    /* Copy-to-clipboard button on code blocks */
    pre .copied {
      color: ${({ theme }) => theme.palette.button.secondary.color};
      background: ${({ theme }) => theme.palette.button.secondary.background};
      border: 1px solid ${({ theme }) => theme.palette.button.secondary.border};
    }

    pre:hover .copied:hover {
      color: ${({ theme }) => theme.palette.button.secondary.color};
      background: ${({ theme }) =>
        theme.palette.button.secondary.hoverBackground};
    }

    pre:hover .copied:active,
    pre .copied.active {
      color: ${({ theme }) => theme.palette.button.secondary.color};
      background: ${({ theme }) =>
        theme.palette.button.secondary.clickedBackground};
    }

    /* Inline code */
    :not(pre) > code {
      background-color: ${({ theme }) => theme.palette.background.paper};
      padding: 0.2em 0.4em;
      border-radius: 3px;
    }

    /* Links */
    a {
      color: ${({ theme }) => theme.palette.primary.main};
      text-decoration: underline;

      &:hover {
        color: ${({ theme }) => theme.palette.primary.dark};
      }
    }

    /* Blockquotes */
    blockquote {
      border-left: 3px solid ${({ theme }) => theme.palette.divider};
      padding-left: 1em;
      margin: 0.5em 0;
      opacity: 0.8;
    }

    /* Horizontal rules */
    hr {
      border: none;
      border-top: 1px solid ${({ theme }) => theme.palette.divider};
      margin: 1em 0;
    }

    /* Tables */
    table {
      border-collapse: collapse;
      margin: 0.5em 0;
    }

    th,
    td {
      border: 1px solid ${({ theme }) => theme.palette.divider};
      padding: 0.5em;
      text-align: left;
    }

    th {
      background-color: ${({ theme }) => theme.palette.primary.dark};
      font-weight: 600;
    }
  }
`;

export const StyledTextField = styled(TextField)`
  flex: 1;

  .MuiOutlinedInput-root {
    background-color: ${({ theme }) => theme.palette.background.input};
  }
`;

export const InputArea = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(2)};
`;
